import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { getDayChipProps } from "pagesV2/pro/Calendar/blackoutUtils"
import styles from "./index.module.css"

/**
 * The full-width bar above the day grid.
 *
 * Red when the day is fully closed, dark for a partial closure or a crew-off day. The
 * bookings count and the reservations jump only appear when something needs action — the
 * "Crew Off" case in the design has neither.
 */
const BlackoutBanner = ({ dayBlackout, onViewDetails, onManageReservations }) => {
  const { t } = useTranslation()
  const chip = getDayChipProps(dayBlackout)

  if (!chip) return null

  const count = dayBlackout.bookings_requiring_action || 0

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="var(--spacing-s)"
      wrap
      className={`${styles.banner} ${styles[chip.variant]}`}
    >
      <Flex align="center" gap="var(--spacing-xs)" className={styles.leading}>
        <Icon name="AlertTriangle" width={20} height={20} color="var(--color-white)" />
        <span className={styles.label}>
          {`${chip.label || t(chip.labelKey, { defaultValue: chip.labelDefault })}${chip.count > 1 ? ` (${chip.count})` : ""}`}
        </span>
        <Button type="primary" size="small" shape="round" onClick={onViewDetails}>
          {t("calendar.blackout.view_details", { defaultValue: "View Details" })}
        </Button>
      </Flex>

      {count > 0 && (
        <Flex align="center" gap="var(--spacing-s)" wrap>
          <span className={styles.actionPill}>
            <Icon name="AlertTriangle" width={16} height={16} color="var(--color-error)" />
            {t("calendar.blackout.bookings_require_action", {
              defaultValue: "{{count}} bookings require action",
              count
            })}
          </span>
          <Button type="primary" size="small" shape="round" onClick={onManageReservations}>
            {t("calendar.blackout.manage_in_reservations", { defaultValue: "Manage in Reservations Page" })}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}

BlackoutBanner.propTypes = {
  /** The day's entry from the blackout feed. Renders nothing when the day is clear. */
  dayBlackout: PropTypes.shape({
    status: PropTypes.string,
    bookings_requiring_action: PropTypes.number,
    blackouts: PropTypes.array
  }),
  onViewDetails: PropTypes.func,
  onManageReservations: PropTypes.func
}

export default BlackoutBanner
